import { useCallback, useEffect, useRef, useState } from 'react';
import { detectCard } from '../services/opencv/cardDetector';

const TARGET_FPS = 15;
const FRAME_INTERVAL = 1000 / TARGET_FPS;

export function useCardDetection(videoRef, isActive) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const lastFrameTime = useRef(0);

  const [detection, setDetection] = useState({
    found: false,
    corners: null,
    areaRatio: 0,
  });

  const processFrame = useCallback(
    (timestamp) => {
      if (!isActive || !videoRef.current || !canvasRef.current) {
        return;
      }

      if (timestamp - lastFrameTime.current < FRAME_INTERVAL) {
        rafRef.current = requestAnimationFrame(processFrame);
        return;
      }
      lastFrameTime.current = timestamp;

      const video = videoRef.current;
      const canvas = canvasRef.current;

      if (video.readyState < video.HAVE_CURRENT_DATA) {
        rafRef.current = requestAnimationFrame(processFrame);
        return;
      }

      const scale = Math.min(1, 480 / video.videoWidth);
      const procW = Math.round(video.videoWidth * scale);
      const procH = Math.round(video.videoHeight * scale);

      canvas.width = procW;
      canvas.height = procH;

      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        rafRef.current = requestAnimationFrame(processFrame);
        return;
      }

      ctx.drawImage(video, 0, 0, procW, procH);

      try {
        const imageData = ctx.getImageData(0, 0, procW, procH);
        const result = detectCard(imageData);

        if (result.found && result.corners) {
          const invScale = 1 / scale;
          setDetection({
            ...result,
            corners: [
              { x: result.corners[0].x * invScale, y: result.corners[0].y * invScale },
              { x: result.corners[1].x * invScale, y: result.corners[1].y * invScale },
              { x: result.corners[2].x * invScale, y: result.corners[2].y * invScale },
              { x: result.corners[3].x * invScale, y: result.corners[3].y * invScale },
            ],
          });
        } else {
          setDetection(result);
        }
      } catch {
        // Silently continue on detection errors
      }

      rafRef.current = requestAnimationFrame(processFrame);
    },
    [isActive, videoRef]
  );

  useEffect(() => {
    if (isActive) {
      rafRef.current = requestAnimationFrame(processFrame);
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isActive, processFrame]);

  return { canvasRef, detection, isReady: true };
}
